import AppContext from 'App/AppContext';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPStack } from 'baseComponents/Stack/IPStack';
import Coach from 'components/CallOuts/Coach';
import { CoachTypes } from 'components/CallOuts/CoachMarkDefinitions';
import { globalAppName } from 'globalConstants';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import logger from 'services/Logging/logService';
import { LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';
import { useNavItems } from './navItems';
import NavMain, { NavPresentation } from './NavMain';
import { navRailClass } from './navMainStyles';
import NavRootOverlay from './NavRootOverlay';

interface INavRootProps {}

export const menuCollapsedWidth = 43;
export const menuExpandedWidth = 180;

// The coachmark announcing the regrouped menu sits after the label of the Frameworks entry, which
// holds Requirements and Controls. On the label, not the icon, so it covers no text.
const frameworksCoachTarget = `.${navRailClass} .ms-Nav-compositeLink:has(.isoplanner-nav-main-standards) .ms-Nav-linkText`;

const NavRoot = (props: INavRootProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['url']);
  const location = useLocation();
  const navItems = useNavItems();
  const [expandedOnHover, setExpandedOnHover] = useState<boolean>(false);
  const [translationMapping] = useState<Record<string, string>>({
    dashboard: t('url:Home'),
    admin: t('url:Admin'),
    controls: t('url:Controls'),
    control: t('url:Control'),
    risks: t('url:Risks'),
    risk: t('url:Risk'),
    standards: t('url:Norms'),
    tasks: t('url:Tasks'),
    library: t('url:Library'),
    theme: t('url:Requirement'),
    themes: t('url:Requirements'),
    schedule: t('url:Schedule'),
    process: t('url:Process'),
    processes: t('url:Processes'),
    objective: t('url:Objective'),
    objectives: t('url:Objectives'),
    kpi: t('url:KPI'),
    kpis: t('url:KPIs'),
    audit: t('url:Audit'),
    audits: t('url:Audits'),
    asset: t('url:Asset'),
    assets: t('url:Assets'),
    partner: t('url:Partner'),
    customer: t('url:Customer'),
    tenant: t('url:Tenant'),
    processingactivities: t('url:ProcessingActivities'),
    processingactivity: t('url:ProcessingActivity'),
    projects: t('url:Projects'),
    project: t('url:Project'),
  });

  useEffect(() => {
    //log all location changes
    let strLocation = location.pathname;
    if (location.hash) {
      strLocation += location.hash;
    }
    if (location.search) {
      strLocation += ' ' + location.search;
    }
    logger.debug('Location: ' + strLocation);
    //set browser tab name
    const locationArray = location.pathname.split('/');
    const navKey = locationArray[1] || 'dashboard';
    const transNavKey = translationMapping[navKey] || navKey;
    if (appContext.user.login.isOrgUnit || appContext.user.login.isGuest) {
      document.title = `${transNavKey} - ${appContext.user.tenant.name} - ${globalAppName}`;
    } else {
      document.title = `${transNavKey} - ${globalAppName}`;
    }
  }, [
    location,
    appContext.user.login.isGuest,
    appContext.user.login.isOrgUnit,
    appContext.user.tenant.name,
    translationMapping,
  ]);

  useEffect(() => {
    setLocalStorageData(appContext, LocalStorageKeys.MainNavCollapsed, appContext.isMainNavCollapsed.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appContext.isMainNavCollapsed]);

  // Picking a page ends the peek: the flyout closes when the location changes.
  useEffect(() => {
    setExpandedOnHover(false);
  }, [location.pathname]);

  const collapsed = appContext.isMainNavCollapsed;
  const presentation: NavPresentation = collapsed ? 'icons' : 'full';
  // The expanded rail shows the coachmark, to users who can see the Frameworks entry. The flyout and
  // the mobile panel do not, so it is not repeated and asked of the server once per expand at most.
  const showMenuCoachmark = !collapsed && navItems.standards.isVisible(appContext);

  //
  // Main render
  //
  // The rail stays mounted while the flyout is open, so what the user expanded survives hovering.
  // The button that collapses and expands it lives in the top bar.
  //
  return (
    <IPStack
      verticalFill
      onMouseEnter={() => {
        if (collapsed) {
          setExpandedOnHover(true);
        }
      }}
      styles={{
        root: {
          position: 'relative',
          width: collapsed ? menuCollapsedWidth : menuExpandedWidth,
          minWidth: collapsed ? menuCollapsedWidth : menuExpandedWidth,
          transition: 'all 0.3s ease',
        },
      }}
    >
      <IPStack.Item grow styles={{ root: { minHeight: 0 } }}>
        <NavMain presentation={presentation} />
        {showMenuCoachmark && (
          <Coach
            target={frameworksCoachTarget}
            type={CoachTypes.MainMenuChanged}
            direction={IPDirectionalHint.rightCenter}
          />
        )}
      </IPStack.Item>
      {collapsed && expandedOnHover && <NavRootOverlay onClose={() => setExpandedOnHover(false)} />}
    </IPStack>
  );
};

export default NavRoot;
